import { readonly, ref } from 'vue'
import { currentSession, refreshSessionOnce } from './client'

/**
 * Client temps réel — connexion unique, partagée par toute l'application.
 *
 * Un seul WebSocket pour tous les écrans, avec comptage de références par sujet :
 * trois composants qui écoutent `items.updated` n'ouvrent pas trois connexions,
 * et le désabonnement du dernier libère le sujet côté serveur.
 *
 * ─── Les cinq choses qu'un client WebSocket naïf oublie ───
 *
 * **1. Se reconnecter.** Une connexion tombe : wifi qui change, proxy qui coupe
 * une session inactive, serveur redéployé. Sans reconnexion automatique,
 * l'application cesse silencieusement de recevoir — et l'utilisateur ne voit
 * qu'un écran qui ne se met plus à jour, sans savoir pourquoi.
 *
 * **2. Attendre entre deux tentatives, de plus en plus longtemps.** Un serveur
 * qui redémarre reçoit sinon des milliers de tentatives par seconde de chaque
 * onglet ouvert — l'application achève ce qu'elle voulait joindre.
 *
 * **3. Ajouter du hasard à cette attente.** Sans lui, tous les onglets de tous
 * les utilisateurs se reconnectent à la MÊME milliseconde après une coupure : le
 * serveur revient et s'écroule aussitôt. C'est le troupeau tonitruant.
 *
 * **4. Se ré-abonner après la reconnexion.** Le serveur ne garde rien : une
 * nouvelle connexion part vierge. Sans ré-abonnement, la reconnexion réussit et
 * plus aucun message n'arrive — le pire des symptômes, puisque l'indicateur dit
 * « connecté ».
 *
 * **5. Détecter une connexion morte sans FIN TCP.** Un portable refermé, un
 * tunnel : la socket reste « ouverte » côté client alors que rien ne passe plus.
 * Le navigateur n'expose pas les `pong` du protocole, on envoie donc un `ping`
 * applicatif et l'on considère la connexion perdue si la réponse n'arrive pas.
 */

export type EtatTempsReel =
  | 'inactif' // aucune session, ou déconnexion demandée
  | 'connexion' // poignée de main en cours
  | 'pret' // authentifié, messages en circulation
  | 'reconnexion' // coupée, nouvelle tentative programmée
  | 'refus' // jeton refusé : inutile de réessayer sans se reconnecter

type Gestionnaire = (donnees: unknown) => void

/** Attente maximale entre deux tentatives. */
const ATTENTE_MAX_MS = 30_000
/** Intervalle du ping applicatif, et délai au-delà duquel on conclut au silence. */
const PING_MS = 25_000
const SILENCE_MAX_MS = PING_MS * 2.5

const etatInterne = ref<EtatTempsReel>('inactif')

/** État de la connexion, en lecture seule pour l'interface. */
export const etatTempsReel = readonly(etatInterne)

const resynchronisationsInterne = ref(0)

/**
 * Nombre de fois où le canal est redevenu « prêt » APRÈS l'avoir déjà été dans
 * la session : coupure réseau, mais aussi rotation de jeton. Le serveur ne
 * rejoue rien de ce qui a été émis entre-temps : chaque écran qui affiche des
 * données doit alors les relire (`useApresReconnexion`).
 *
 * Un compteur et non la transition `reconnexion → pret` : la rotation de jeton
 * repasse par `connexion` (compteur de tentatives remis à zéro), et les écrans
 * qui guettaient la transition ne voyaient jamais cette reprise-là.
 */
export const resynchronisations = readonly(resynchronisationsInterne)

/**
 * URL du WebSocket, dérivée de celle de l'API.
 *
 * `http` → `ws`, `https` → `wss`. La dériver plutôt que d'ajouter une variable
 * d'environnement évite le cas où l'API pointe la production et le WebSocket le
 * développement — un décalage silencieux, puisque les deux « fonctionnent ».
 */
function urlWebSocket(): string {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://127.0.0.1:3000'
  return `${base.replace(/^http/, 'ws').replace(/\/+$/, '')}/api/realtime`
}

class ClientTempsReel {
  private socket: WebSocket | null = null
  /** Sujet → gestionnaires. La taille de l'ensemble EST le compteur de références. */
  private readonly abonnes = new Map<string, Set<Gestionnaire>>()
  private tentatives = 0
  private minuteurReconnexion: number | null = null
  private minuteurPing: number | null = null
  private dernierMessage = 0
  /** Déconnexion VOULUE : interdit la reconnexion automatique. */
  private arretDemande = false
  /**
   * Une rotation de jeton est en cours : le gestionnaire de `close` doit laisser
   * faire, sinon il programmerait une reconnexion avec le jeton qui vient
   * d'être refusé.
   */
  private rotationEnCours = false
  /**
   * Une rotation a DÉJÀ été tentée dans cet épisode de récupération.
   *
   * Remis à zéro seulement quand la connexion redevient « prête ». C'est ce qui
   * distingue « premier refus, un jeton neuf va régler ça » de « le jeton neuf a
   * été refusé aussi » — deuxième cas où insister boucle indéfiniment, chaque
   * rotation réussissant côté HTTP et échouant côté canal (horloge décalée,
   * secret de signature changé).
   */
  private rotationTentee = false
  /** Le canal a déjà été prêt dans cette session : le prochain « prêt » est une reprise. */
  private aEtePret = false
  /**
   * Appelé quand le serveur ferme le canal pour changement d'organisation.
   *
   * Posé par le store d'authentification, qui est le seul à savoir relire un
   * profil. Le client temps réel, lui, ne connaît ni l'API ni la session : il
   * signale, il ne décide pas.
   */
  private surOrganisationChangee: (() => void) | null = null

  /** Déclare le rappel ci-dessus. Idempotent : le dernier posé gagne. */
  ecouterChangementOrganisation(rappel: () => void): void {
    this.surOrganisationChangee = rappel
  }

  /**
   * Ouvre la connexion si une session existe. Idempotent : appeler deux fois
   * n'ouvre pas deux sockets.
   */
  connect(): void {
    this.arretDemande = false

    if (currentSession() === null) {
      // Pas de session : on ne tente rien. Le serveur fermerait de toute façon
      // au bout de dix secondes faute d'authentification.
      etatInterne.value = 'inactif'
      return
    }
    if (this.socket !== null && this.socket.readyState <= WebSocket.OPEN) return

    etatInterne.value = this.tentatives === 0 ? 'connexion' : 'reconnexion'

    const socket = new WebSocket(urlWebSocket())
    this.socket = socket

    socket.addEventListener('open', () => {
      // Authentification par MESSAGE et non par l'URL : un jeton dans la chaîne
      // de requête part dans les journaux d'accès du serveur, du proxy, et dans
      // l'historique du navigateur. Voir portail/realtime.ts côté serveur.
      const session = currentSession()
      if (session === null) {
        socket.close(1000, 'session perdue')
        return
      }
      this.envoyer({ type: 'auth', token: session.accessToken })
    })

    socket.addEventListener('message', (evenement) => {
      this.dernierMessage = Date.now()
      this.traiter(evenement.data as string)
    })

    // Le paramètre est optionnel à dessein : les doubles de WebSocket des tests
    // déclenchent l'événement sans charge, et une lecture directe planterait sur
    // un chemin qui n'a rien à voir avec ce qu'on éprouve.
    socket.addEventListener('close', (evenement?: CloseEvent) => {
      this.arreterPing()
      this.socket = null
      if (this.arretDemande) {
        etatInterne.value = 'inactif'
        return
      }
      // ─── L'organisation effective a changé sous nos pieds ───
      //
      // Le serveur ferme ce canal en `1008 organisation_changed` quand
      // l'organisation de la session n'est plus celle du canal — ce qui arrive
      // à CHAQUE fin de jump backoffice (échéance, révocation d'un droit), et le
      // serveur ne le dit nulle part ailleurs : les requêtes HTTP continuent de
      // répondre 200, dans l'organisation d'origine.
      //
      // Sans cette relecture, l'interface garderait l'ancien profil : le bandeau
      // continuerait d'affirmer « vous êtes chez le client X » pendant que
      // chaque requête lirait et ÉCRIRAIT chez l'exploitant.
      //
      // On ne décide de rien ici : on redemande son profil au serveur, qui fait
      // autorité. La reconnexion suit son cours normalement.
      if (evenement?.code === 1008 && evenement.reason === 'organisation_changed') {
        this.surOrganisationChangee?.()
      }
      // Un rafraîchissement en cours décide lui-même de la suite : programmer une
      // reconnexion ici la lancerait avec le jeton périmé, qui vient justement
      // d'être refusé.
      if (this.rotationEnCours || etatInterne.value === 'refus') return
      this.programmerReconnexion()
    })

    socket.addEventListener('error', () => {
      // Rien à faire ici : un `error` est TOUJOURS suivi d'un `close`, et c'est
      // là que la reconnexion se décide. Traiter les deux reprogrammerait deux
      // tentatives pour une seule coupure.
    })
  }

  /** Ferme la connexion et interdit la reconnexion. Appelé à la déconnexion. */
  disconnect(): void {
    this.arretDemande = true
    this.rotationEnCours = false
    this.rotationTentee = false
    // Une nouvelle session repart d'un chargement complet : sa première
    // connexion n'est pas une reprise.
    this.aEtePret = false
    this.annulerReconnexion()
    this.arreterPing()
    this.socket?.close(1000, 'déconnexion')
    this.socket = null
    etatInterne.value = 'inactif'
  }

  /**
   * Abonne un gestionnaire à un sujet. Retourne la fonction de désabonnement —
   * à appeler au démontage du composant, sans quoi le gestionnaire continue de
   * tourner sur un composant disparu.
   */
  subscribe(sujet: string, gestionnaire: Gestionnaire): () => void {
    let ensemble = this.abonnes.get(sujet)
    const premier = ensemble === undefined

    if (ensemble === undefined) {
      ensemble = new Set<Gestionnaire>()
      this.abonnes.set(sujet, ensemble)
    }
    ensemble.add(gestionnaire)

    // On ne parle au serveur QUE pour le premier abonné d'un sujet : les
    // suivants sont un simple ajout local.
    if (premier && etatInterne.value === 'pret') {
      this.envoyer({ type: 'subscribe', topics: [sujet] })
    }
    // Aucune session encore ouverte : la connexion se fait au premier abonné,
    // pour qu'un écran qui écoute suffise à établir le lien.
    this.connect()

    return () => {
      const courant = this.abonnes.get(sujet)
      if (courant === undefined) return
      courant.delete(gestionnaire)
      if (courant.size > 0) return

      this.abonnes.delete(sujet)
      if (etatInterne.value === 'pret') {
        this.envoyer({ type: 'unsubscribe', topics: [sujet] })
      }
    }
  }

  private traiter(brut: string): void {
    let message: { type?: string; topic?: string; payload?: unknown; code?: string }
    try {
      message = JSON.parse(brut)
    } catch {
      return // message illisible : on ignore plutôt que de casser la connexion
    }

    switch (message.type) {
      case 'ready': {
        this.tentatives = 0
        // Épisode de récupération terminé : une prochaine expiration aura droit
        // à sa propre rotation.
        this.rotationTentee = false
        this.rotationEnCours = false
        etatInterne.value = 'pret'
        if (this.aEtePret) resynchronisationsInterne.value++
        this.aEtePret = true
        // Ré-abonnement à TOUT : le serveur ne garde rien d'une connexion
        // précédente. Sans cela, la reconnexion réussit et plus aucun message
        // n'arrive — alors que l'indicateur dit « connecté ».
        const sujets = [...this.abonnes.keys()]
        if (sujets.length > 0) this.envoyer({ type: 'subscribe', topics: sujets })
        this.demarrerPing()
        return
      }
      case 'event': {
        if (typeof message.topic !== 'string') return
        for (const gestionnaire of this.abonnes.get(message.topic) ?? []) {
          try {
            gestionnaire(message.payload)
          } catch (erreur) {
            // Un gestionnaire qui lève ne doit pas empêcher les autres de
            // recevoir, ni tuer la connexion.
            console.error(`temps réel : gestionnaire de « ${message.topic} » en échec`, erreur)
          }
        }
        return
      }
      case 'error': {
        // `token_expired` : le serveur révalide les connexions ouvertes et ferme
        // celles dont le jeton a expiré — un canal ne doit pas survivre à son
        // jeton. `unauthenticated` : la poignée de main a été refusée.
        //
        // Dans les deux cas, se reconnecter avec le MÊME jeton échouerait à
        // l'identique. Mais rester en « refus » serait tout aussi faux : le canal
        // resterait mort jusqu'à la prochaine requête HTTP qui, elle,
        // rafraîchirait. On tente donc UNE rotation, puis on revient.
        if (message.code === 'unauthenticated' || message.code === 'token_expired') {
          // ORDRE IMPORTANT : les drapeaux et l'état AVANT la fermeture. Le
          // gestionnaire de `close` s'exécute de façon synchrone ; s'il tourne
          // pendant que les drapeaux valent encore « rien en cours », il
          // programme une reconnexion avec le jeton qui vient d'être refusé —
          // exactement ce qu'on cherche à éviter.
          if (this.rotationTentee) {
            // Le jeton neuf a été refusé lui aussi : rafraîchir encore ne
            // changerait rien, et chaque rotation réussit côté HTTP — la boucle
            // serait donc infinie et invisible.
            etatInterne.value = 'refus'
            this.socket?.close(1000, 'jeton refusé')
            return
          }
          this.rotationTentee = true
          this.rotationEnCours = true
          etatInterne.value = 'reconnexion'
          this.socket?.close(1000, 'jeton à renouveler')
          void refreshSessionOnce()
            .then(() => {
              // Compteur remis à zéro : ce n'est pas une panne réseau, la
              // prochaine tentative doit partir tout de suite.
              this.tentatives = 0
              this.rotationEnCours = false
              this.connect()
            })
            .catch(() => {
              // Le jeton de rafraîchissement est mort lui aussi : l'intercepteur
              // HTTP déconnectera proprement à la prochaine requête. Insister
              // ici ne ferait que marteler le serveur.
              this.rotationEnCours = false
              etatInterne.value = 'refus'
            })
        }
        return
      }
      default:
        return // 'pong', 'subscribed' : rien à faire de plus que la date de vie
    }
  }

  private envoyer(charge: unknown): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return
    this.socket.send(JSON.stringify(charge))
  }

  /**
   * Programme une reconnexion avec report exponentiel ET hasard.
   *
   * Le hasard n'est pas cosmétique : sans lui, tous les onglets de tous les
   * utilisateurs se reconnectent à la même milliseconde après une coupure, et le
   * serveur qui vient de revenir s'écroule sous la rafale.
   */
  private programmerReconnexion(): void {
    this.annulerReconnexion()
    this.tentatives++
    etatInterne.value = 'reconnexion'

    const base = Math.min(ATTENTE_MAX_MS, 500 * 2 ** Math.min(this.tentatives, 6))
    const attente = base / 2 + Math.random() * (base / 2)

    this.minuteurReconnexion = window.setTimeout(() => {
      this.minuteurReconnexion = null
      this.connect()
    }, attente)
  }

  private annulerReconnexion(): void {
    if (this.minuteurReconnexion !== null) {
      window.clearTimeout(this.minuteurReconnexion)
      this.minuteurReconnexion = null
    }
  }

  /**
   * Ping applicatif.
   *
   * Le navigateur ne donne aucun accès aux `ping`/`pong` du protocole : on ne
   * peut donc pas savoir si le serveur répond encore. Un ping applicatif, lui,
   * revient en `pong` et remet à jour la date de dernier message. Passé
   * `SILENCE_MAX_MS` sans rien, on considère la connexion morte et on la
   * remplace — c'est le seul moyen de détecter un tunnel ou un portable refermé.
   */
  private demarrerPing(): void {
    this.arreterPing()
    this.dernierMessage = Date.now()

    this.minuteurPing = window.setInterval(() => {
      if (Date.now() - this.dernierMessage > SILENCE_MAX_MS) {
        // `close` déclenche l'écouteur, qui programme la reconnexion.
        this.socket?.close(4000, 'silence')
        return
      }
      this.envoyer({ type: 'ping' })
    }, PING_MS)
  }

  private arreterPing(): void {
    if (this.minuteurPing !== null) {
      window.clearInterval(this.minuteurPing)
      this.minuteurPing = null
    }
  }
}

/** Instance unique de l'application. */
export const realtime = new ClientTempsReel()
